import {
  Address,
  HostDashboardGroupBookingsQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Papa from 'papaparse';
import { Button, Tab, Tabs } from 'react-bootstrap';
import countries from '../../../../dataObjects/countries';
import DietaryRequirementsTable from './DietaryRequirementsTable';
import EmergencyContactTable from './EmergencyContactTable';
import GuestsTable, { getBalanceOutstandingForHosts } from './GuestsTable';
import HostDashboardGuestMeetPointsTable from './HostDashboardGuestMeetPointsTable';
import MedicalInformationTable from './MedicalInformationTable';
import PassportTable from './PassportTable';
import TravelInformationTable from './TravelInformationTable';
import TravelInsuranceTable from './TravelInsuranceTable';

/**
 * Hosts that don't have email addresses hidden
 *
 * We want hosts to use the chat system only, but a few have issues that we are addressing before disabling. As soon as
 * those issues are fixed for a host in this list, we should simply remove them from the list here and it will be
 * updated everywhere.
 */
export const hostsThatDontHaveEmailDisabled: string[] = [
  'nordic-ventures', // Nordic Ventures
];

/**
 * Get active spaces with the booking owner
 */
export function getActiveSpacesWithOwner(
  bookings: HostDashboardGroupBookingsQuery['group']['bookings']['items'],
): SpaceWithOwner[] {
  const spacesWithOwner: SpaceWithOwner[] = [];

  bookings.forEach((booking) =>
    booking.spaces.forEach((space) => {
      spacesWithOwner.push({
        ...space,
        owner: booking.user,
      });
    }),
  );

  return spacesWithOwner.filter((i) => i.active);
}

/**
 *
 * formatAddressForCSVExport
 *
 * Format the address for a single address field in the CSV
 *
 */
export function formatAddressForCSVExport(address: Address): string {
  const lines = [
    address?.addressLine1,
    address?.addressLine2,
    address?.locality,
    address?.dependentLocality,
    address?.administrativeArea,
    address?.postalCode,
    address?.country ? countries[address.country] : '',
  ];

  return lines.filter((i) => i && i !== '').join(', ');
}

/**
 * Format the CSV Download
 *
 * We offer a CSV download so that hosts can get all the information in one go, to add to their systems. This sets
 * sensible field names, to be used with `Array.map` on all active spaces.
 */
export function formatCSV(space: SpaceWithOwner, showEmails: boolean) {
  const { guest } = space;
  const { tripForm } = guest;

  let emails = {};
  if (showEmails) {
    emails = {
      'Guest Email': guest.email ?? undefined,
      'Booking Owner Email': space.owner.email,
    };
  }

  return {
    // Guest details
    'Booking Owner': `${space.owner.givenName} ${space.owner.familyName}`,

    Guest: guest.givenName
      ? `${guest.givenName} ${guest.familyName}`
      : undefined,
    'Booking ID': space.booking.id,
    'Space ID': space.id,
    ...emails,
    Phone: guest.phone ?? undefined,
    status:
      getBalanceOutstandingForHosts({
        spaces: [space],
      }) > 0
        ? 'Balance Due'
        : 'Paid',
    'Trip Form Status': space.guest.completed ? 'Completed' : 'Incomplete',
    // Travel Information
    'Arrival Time': tripForm?.travelInformation?.arrivalTime
      ? DateTime.fromISO(
          tripForm?.travelInformation?.arrivalTime,
        ).toLocaleString(DateTime.DATETIME_MED)
      : undefined,
    'Arrival Transport': tripForm?.travelInformation?.arrivalTransportMode,
    'Arrival Flight': tripForm?.travelInformation?.arrivalFlightNumber,
    'Departure Time': tripForm?.travelInformation?.departureTime
      ? DateTime.fromISO(
          tripForm?.travelInformation?.departureTime,
        ).toLocaleString(DateTime.DATETIME_MED)
      : undefined,
    'Departure Transport': tripForm?.travelInformation?.departureTransportMode,
    'Departure Flight': tripForm?.travelInformation?.departureFlightNumber,
    // Additional info
    'Additional Info': tripForm?.additionalInformation,
    // medical information
    'Medical Info': tripForm?.medicalInformation,
    // dietary requirements
    'Dietary Requirements': tripForm?.dietaryRequirements,
    // Emergency Contact
    'Emergency Contact Name': tripForm?.emergencyContact?.name,
    'Emergency Contact Phone': tripForm?.emergencyContact?.phone,
    'Emergency Contact Relationship': tripForm?.emergencyContact?.relationship,
    // Travel insurance
    'Travel Insurance Company': tripForm?.travelInsurance?.company,
    'Travel Insurance Phone': tripForm?.travelInsurance?.phone,
    'Travel Insurance Policy Number': tripForm?.travelInsurance?.policyNumber,
    // Passport
    'Passport First Name': tripForm?.passportInformation?.givenName,
    'Passport Last Name': tripForm?.passportInformation?.familyName,
    'Passport Number': tripForm?.passportInformation?.passportNumber,
    'Passport Nationality': tripForm?.passportInformation?.nationality,
    'Passport Issue Date': tripForm?.passportInformation?.issueDate,
    'Passport Expiry Date': tripForm?.passportInformation?.expiryDate,
    'Date of Birth': tripForm?.passportInformation?.dateOfBirth,
    Gender: tripForm?.passportInformation?.gender,
    // Meet points
    'Meet Point Time': tripForm?.meetPoint?.arriveAt
      ? DateTime.fromISO(tripForm.meetPoint.arriveAt.localTime).toFormat(
          'HH:mm',
        )
      : undefined,
    'Meet Point Address': formatAddressForCSVExport(
      tripForm?.meetPoint?.address,
    ),
    // Departure points
    'Departure Point Time': tripForm?.departPoint?.arriveAt
      ? DateTime.fromISO(tripForm.departPoint.arriveAt.localTime).toFormat(
          'HH:mm',
        )
      : undefined,
    'Departure Point Address': formatAddressForCSVExport(
      tripForm?.departPoint?.address,
    ),
    Notes: space.guest.hostNotes?.split('\n').join(' ').trim(),
  };
}

/**
 * Download CSV button
 */
export function DownloadCSV({
  spaces,
  showEmails,
}: {
  spaces: SpaceWithOwner[];
  showEmails: boolean;
}) {
  const csvName = 'groupBookings.csv';

  function downloadCsv() {
    // Create the CSV raw file (as a blob)
    const flattenedArray = spaces.map((i) => formatCSV(i, showEmails));
    const csv = Papa.unparse(flattenedArray);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

    // Automatically download
    if ((window.navigator as any).msSaveOrOpenBlob)
      // IE hack; see http://msdn.microsoft.com/en-us/library/ie/hh779016.aspx
      (window.navigator as any).msSaveBlob(blob, csvName);
    else {
      // Otherwise create an <a> element and click on it
      const a = window.document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.type = 'text/csv';
      a.download = csvName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  return (
    <div className="d-grid gap-2">
      <Button onClick={downloadCsv}>Download CSV</Button>
    </div>
  );
}

/**
 * List of Bookings for a specific group
 *
 * Designed for the host dashboard - this component shows a list of all the bookings alongside some key guest
 * information (e.g. names, flight details...).
 */
export default function GroupBookingsList({
  group,
}: {
  group: HostDashboardGroupBookingsQuery['group'];
}) {
  // Get the spaces
  const spaces = getActiveSpacesWithOwner(group.bookings.items);
  const isGroupDeparted =
    DateTime.fromISO(group?.start.utc, { zone: 'utc' }).diffNow().as('days') <
    1;
  const { query } = useRouter();
  const { hostURL, groupID } = query;

  // Show email
  const showEmails = hostsThatDontHaveEmailDisabled.includes(hostURL as string);

  // Show the tabs
  return (
    <>
      <Tabs defaultActiveKey="guests">
        <Tab eventKey="guests" title="Guests" className="pt-3">
          <GuestsTable
            spaces={spaces}
            host={group.trip.host}
            showEmails={showEmails}
          />
        </Tab>
        <Tab eventKey="travelInformation" title="Travel" className="pt-3">
          <TravelInformationTable spaces={spaces} />
        </Tab>
        <Tab eventKey="travelInsurance" title="Insurance" className="pt-3">
          <TravelInsuranceTable spaces={spaces} />
        </Tab>
        <Tab eventKey="passportInformation" title="Passport" className="pt-3">
          <PassportTable spaces={spaces} />
        </Tab>
        <Tab eventKey="emergencyContact" title="Emergency" className="pt-3">
          <EmergencyContactTable spaces={spaces} />
        </Tab>
        <Tab
          eventKey="medicationInformation"
          title="Medical Information"
          className="pt-3"
        >
          <MedicalInformationTable spaces={spaces} />
        </Tab>
        <Tab
          eventKey="dietaryRequirements"
          title="Dietary Requirements"
          className="pt-3"
        >
          <DietaryRequirementsTable spaces={spaces} />
        </Tab>
        <Tab eventKey="meetPoints" title="Meet/Depart Points" className="pt-3">
          <HostDashboardGuestMeetPointsTable spaces={spaces} />
        </Tab>
      </Tabs>
      {spaces[0] && <DownloadCSV spaces={spaces} showEmails={showEmails} />}
      {spaces[0] && (
        <div className="d-grid gap-2 mt-2">
          <Link
            passHref
            href={`/hosts/dashboard/${hostURL}/bookings/${groupID}/message`}
            legacyBehavior
          >
            <Button>Send Message to All Guests</Button>
          </Link>
        </div>
      )}
      {spaces.length > 0 && isGroupDeparted && (
        <div className="d-grid gap-2 mt-2">
          <Link passHref href={`/guest/photos/${groupID}`} legacyBehavior>
            <Button>Upload photos for this trip</Button>
          </Link>
        </div>
      )}
    </>
  );
}

/**
 * Guest details alongside the booking owner
 */
export type SpaceWithOwner =
  HostDashboardGroupBookingsQuery['group']['bookings']['items'][0]['spaces'][0] & {
    owner: HostDashboardGroupBookingsQuery['group']['bookings']['items'][0]['user'];
  };
